import React, { useState, useMemo } from 'react';
import { Card, Button, Badge, Modal, Input } from '../components/ui';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useDatabase } from '../hooks/useDatabase';
import { getSizeCategory, getSizeCategoryLabel, SIZE_RANGES, SIZE_LABELS } from '../utils/trasterosSize';

const EMPTY = {
  codigo: '',
  zona: '',
  categoria: 'mini',
  precio_mensual: '',
  estado: 'disponible',
  observaciones: '',
  ancho: '',
  fondo: '',
  size: ''
};

const SIZE_BADGE_CLASSES = {
  mini_pilar: 'bg-slate-100 text-slate-700 border-slate-300',
  mini: 'bg-sky-100 text-sky-700 border-sky-300',
  pequeño_eco: 'bg-teal-100 text-teal-700 border-teal-300',
  pequeño: 'bg-violet-100 text-violet-700 border-violet-300',
  pequeño_plus: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300',
  mediano: 'bg-amber-100 text-amber-700 border-amber-300',
  mediano_plus: 'bg-orange-100 text-orange-700 border-orange-300',
  grande: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  xl: 'bg-rose-100 text-rose-700 border-rose-300',
};

const CATEGORY_DESCRIPTIONS = {
  mini_pilar: 'De 0.80 a 0.90 m²',
  mini: 'De 0.91 a 1.00 m²',
  pequeño_eco: 'De 1.01 a 1.50 m²',
  pequeño: 'De 1.51 a 1.70 m²',
  pequeño_plus: 'De 1.71 a 2.00 m²',
  mediano: 'De 2.01 a 2.50 m²',
  mediano_plus: 'De 2.51 a 3.00 m²',
  grande: 'De 3.01 a 3.60 m²',
  xl: 'De 3.61 a 4.00 m²',
};

const SizeBadge = ({ categoria }) => {
  const cls = SIZE_BADGE_CLASSES[categoria] || 'bg-gray-100 text-gray-600 border-gray-200';
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${cls}`}>
      {SIZE_LABELS[categoria] || categoria}
    </span>
  );
};

const SortIcon = ({ column, sortConfig }) => {
  if (sortConfig.key !== column) return <SafeIcon icon={FiIcons.FiChevronsUpDown} className="ml-1 text-gray-300 text-xs" />;
  return sortConfig.dir === 'asc' 
    ? <SafeIcon icon={FiIcons.FiChevronUp} className="ml-1 text-brand-primary text-xs" />
    : <SafeIcon icon={FiIcons.FiChevronDown} className="ml-1 text-brand-primary text-xs" />;
};

const Th = ({ label, column, sortConfig, onSort }) => (
  <th 
    className="px-6 py-3 font-medium cursor-pointer select-none hover:text-brand-dark transition-colors"
    onClick={() => onSort(column)}
  >
    <span className="flex items-center whitespace-nowrap">
      {label}
      <SortIcon column={column} sortConfig={sortConfig} />
    </span>
  </th>
);

const Trasteros = () => {
  const { data, loading, error, refresh, insert, update, remove } = useDatabase('trasteros');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'codigo', dir: 'asc' });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const normalizedData = useMemo(() => data.map(t => ({
    ...t,
    categoria: t.categoria || getSizeCategory(t.size) || 'mini',
  })), [data]);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const rows = normalizedData.filter(t => 
      (t.codigo?.toLowerCase().includes(term) || t.zona?.toLowerCase().includes(term) || t.estado?.toLowerCase().includes(term)) &&
      (filterCat === '' || t.categoria === filterCat)
    );

    return [...rows].sort((a, b) => {
      const av = a[sortConfig.key] ?? '';
      const bv = b[sortConfig.key] ?? '';
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv), 'es', { numeric: true });
      return sortConfig.dir === 'asc' ? cmp : -cmp;
    });
  }, [normalizedData, searchTerm, filterCat, sortConfig]);

  const openNew = () => {
    setForm(EMPTY);
    setEditingId(null);
    setIsModalOpen(true);
  };

  const openEdit = (item) => {
    setForm({
      codigo: item.codigo,
      zona: item.zona,
      categoria: item.categoria || getSizeCategory(item.size) || 'mini',
      precio_mensual: item.precio_mensual,
      estado: item.estado,
      observaciones: item.observaciones || '',
      ancho: item.ancho || '',
      fondo: item.fondo || '',
      size: item.size || ''
    });
    setEditingId(item.id);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setForm(EMPTY);
    setEditingId(null);
  };

  const handleDimensionChange = (field, value) => {
    const newForm = { ...form, [field]: value };
    const ancho = parseFloat(newForm.ancho) || 0;
    const fondo = parseFloat(newForm.fondo) || 0;
    
    const sizeCalc = parseFloat((ancho * fondo).toFixed(2));
    newForm.size = sizeCalc || '';

    if (sizeCalc > 0) {
      const newCat = getSizeCategory(sizeCalc);
      if (newCat) {
        newForm.categoria = newCat;
        const rangeData = SIZE_RANGES.find(r => r.key === newCat);
        if (rangeData) newForm.precio_mensual = rangeData.price;
      }
    }

    setForm(newForm);
  };

  const handleManualCategoryChange = (catKey) => {
    const rangeData = SIZE_RANGES.find(r => r.key === catKey);
    setForm({
      ...form, 
      categoria: catKey,
      precio_mensual: rangeData ? rangeData.price : form.precio_mensual
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        size: parseFloat(form.size) || 0,
        ancho: parseFloat(form.ancho) || 0,
        fondo: parseFloat(form.fondo) || 0,
        precio_mensual: parseFloat(form.precio_mensual) || 0,
      };

      if (editingId) await update(editingId, payload);
      else await insert(payload);
      
      closeModal();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este trastero?')) return;
    try {
      await remove(id);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const cols = [
    { label: 'Código', key: 'codigo' },
    { label: 'Zona', key: 'zona' },
    { label: 'Categoría', key: 'categoria' },
    { label: 'Superficie', key: 'size' },
    { label: 'Precio', key: 'precio_mensual' },
    { label: 'Estado', key: 'estado' },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-brand-dark flex items-center gap-2">
            Gestión de Trasteros
            {loading && <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-brand-primary text-lg" />}
          </h2>
          <p className="text-sm sm:text-base text-brand-text">{data.length} trasteros · {filtered.length} mostrados</p>
        </div>
        <Button onClick={openNew} className="w-full sm:w-auto flex justify-center items-center gap-2">
          <SafeIcon icon={FiIcons.FiPlus} />
          Nuevo Trastero
        </Button>
      </div>

      {/* Leyenda de Tamaños con Scroll horizontal en móviles muy pequeños o grid adaptativo */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
        {SIZE_RANGES.map(r => {
          const count = normalizedData.filter(t => t.categoria === r.key).length;
          return (
            <button 
              key={r.key} 
              onClick={() => setFilterCat(prev => prev === r.key ? '' : r.key)}
              className={`p-2 sm:p-3 rounded-xl border text-left transition-all ${filterCat === r.key ? 'border-brand-primary bg-brand-light shadow-sm' : 'border-gray-200 bg-white hover:border-brand-primary/40'}`}
            >
              <div className="flex flex-col mb-1 gap-1">
                <div className="overflow-hidden"><SizeBadge categoria={r.key} /></div>
                <span className="text-base sm:text-lg font-bold text-brand-dark mt-1">{count} <span className="text-[10px] font-normal text-gray-400">unids.</span></span>
              </div>
              <p className="text-[10px] sm:text-[11px] text-brand-text leading-tight">{CATEGORY_DESCRIPTIONS[r.key]} <br/><span className="font-medium">{r.price}€/mes</span></p>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-3 text-sm">
          <SafeIcon icon={FiIcons.FiAlertTriangle} /><span>Error: {error}</span>
        </div>
      )}

      <Card>
        <div className="p-3 sm:p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="relative w-full sm:max-w-md">
            <SafeIcon icon={FiIcons.FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Buscar trastero..." 
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-primary"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex w-full sm:w-auto items-center gap-2 justify-between">
            {(searchTerm || filterCat) && (
              <button onClick={() => { setSearchTerm(''); setFilterCat(''); }} className="text-gray-400 hover:text-gray-600 text-sm">
                Limpiar
              </button>
            )}
            <Button variant="ghost" onClick={refresh} title="Actualizar" className="ml-auto">
              <SafeIcon icon={FiIcons.FiRefreshCw} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto hide-scrollbar">
          {!loading && filtered.length === 0 ? (
            <p className="text-center text-brand-text py-12 text-sm">
              {searchTerm || filterCat ? 'No hay resultados para los filtros aplicados.' : 'No hay trasteros registrados.'}
            </p>
          ) : (
            <table className="w-full text-left text-sm min-w-[600px]">
              <thead className="bg-gray-50 text-brand-text">
                <tr>
                  {cols.map(c => (
                    <Th key={c.key} label={c.label} column={c.key} sortConfig={sortConfig} onSort={handleSort} />
                  ))}
                  <th className="px-6 py-3 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-brand-dark">{item.codigo}</td>
                    <td className="px-6 py-4 text-brand-text">{item.zona}</td>
                    <td className="px-6 py-4"><SizeBadge categoria={item.categoria} /></td>
                    <td className="px-6 py-4">
                      {item.size ? (
                        <div className="flex flex-col">
                          <span className="font-medium text-brand-dark">{item.size} m²</span>
                          {(item.ancho > 0 && item.fondo > 0) && (
                            <span className="text-xs text-brand-text/70">{item.ancho}m × {item.fondo}m</span>
                          )}
                        </div>
                      ) : '—'}
                    </td>
                    <td className="px-6 py-4 text-brand-text">{item.precio_mensual}€</td>
                    <td className="px-6 py-4"><Badge variant={item.estado}>{item.estado}</Badge></td>
                    <td className="px-6 py-4 text-right flex items-center justify-end gap-2">
                      <button className="text-gray-400 hover:text-brand-primary p-2 sm:p-1" onClick={() => openEdit(item)} title="Editar">
                        <SafeIcon icon={FiIcons.FiEdit2} />
                      </button>
                      <button className="text-gray-400 hover:text-brand-alert p-2 sm:p-1" onClick={() => handleDelete(item.id)} title="Eliminar">
                        <SafeIcon icon={FiIcons.FiTrash2} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {filtered.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-100 text-xs text-gray-400 flex items-center gap-1">
            <SafeIcon icon={FiIcons.FiInfo} /> Ordenado por <strong className="text-brand-text">{cols.find(c => c.key === sortConfig.key)?.label}</strong>
          </div>
        )}
      </Card>

      <Modal 
        isOpen={isModalOpen} 
        onClose={closeModal}
        title={editingId ? 'Editar Trastero' : 'Añadir Nuevo Trastero'}
      >
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Código" placeholder="Ej: T-015" value={form.codigo} onChange={e => setForm({...form, codigo: e.target.value})} required />
            <Input label="Zona/Planta" placeholder="Ej: Zona 1" value={form.zona} onChange={e => setForm({...form, zona: e.target.value})} required />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-gray-50 p-3 rounded-lg border border-gray-100">
            <Input label="Ancho (m)" type="number" step="0.01" min="0" placeholder="0.00" value={form.ancho} onChange={e => handleDimensionChange('ancho', e.target.value)} />
            <Input label="Fondo (m)" type="number" step="0.01" min="0" placeholder="0.00" value={form.fondo} onChange={e => handleDimensionChange('fondo', e.target.value)} />
            <Input label="Superficie (m²)" type="number" step="0.01" placeholder="0.00" value={form.size} readOnly className="bg-gray-200/50 text-gray-600 font-medium cursor-not-allowed" />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-brand-text mb-2">Categoría de tamaño</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SIZE_RANGES.map(r => (
                <button 
                  key={r.key} 
                  type="button"
                  onClick={() => handleManualCategoryChange(r.key)}
                  className={`p-2 rounded-lg border text-left transition-all ${form.categoria === r.key ? 'border-brand-primary bg-brand-light ring-1 ring-brand-primary' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                >
                  <div className="flex items-center gap-2 mb-0.5 overflow-hidden">
                    <SizeBadge categoria={r.key} />
                  </div>
                  <p className="text-[10px] text-brand-text mt-1">{CATEGORY_DESCRIPTIONS[r.key]}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Precio mensual (€)" type="number" step="0.01" placeholder="0.00" value={form.precio_mensual} onChange={e => setForm({...form, precio_mensual: e.target.value})} required />
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text mb-1">Estado</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary" value={form.estado} onChange={e => setForm({...form, estado: e.target.value})}>
                <option value="disponible">Disponible</option>
                <option value="reservado">Reservado</option>
                <option value="ocupado">Ocupado</option>
                <option value="mantenimiento">En mantenimiento</option>
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-brand-text mb-1">Observaciones</label>
            <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary text-sm" rows={2} value={form.observaciones} onChange={e => setForm({...form, observaciones: e.target.value})} />
          </div>

          <div className="pt-4 flex flex-col-reverse sm:flex-row justify-end gap-3 border-t border-gray-100 mt-4">
            <Button variant="ghost" onClick={closeModal} type="button" className="w-full sm:w-auto">Cancelar</Button>
            <Button type="submit" disabled={saving} className="w-full sm:w-auto">
              {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Guardar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Trasteros;